import React, { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { api, Group, Player } from "../api/client";
import { GymDuty, gymDutiesApi } from "../api/gymDuties";
import { useConfirm } from "../components/ConfirmDialog";
import PageLoader from "../components/PageLoader";
import "./GymDutiesPage.css";

// "2026-09-15" → "15.09.2026", from the string: new Date() would shift the day
// west of Greenwich.
function formatDate(value: string): string {
  const [year, month, day] = value.slice(0, 10).split("-");
  return year && month && day ? `${day}.${month}.${year}` : value;
}

function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function assignedByLabel(duty: GymDuty): string {
  if (!duty.player_id) return "—";
  if (duty.assigned_by === "player") return "Сам записался";
  if (duty.assigned_by === "coach") return "Тренер или админ";
  return "—";
}

const byName = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name, "ru");

export default function GymDutiesPage() {
  const [groups, setGroups] = useState<Group[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [groupID, setGroupID] = useState("");
  const [month, setMonth] = useState(currentMonth);
  const [duties, setDuties] = useState<GymDuty[]>([]);
  const [loading, setLoading] = useState(true);
  const [dutiesLoading, setDutiesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [date, setDate] = useState("");
  const [playerID, setPlayerID] = useState("");
  const [note, setNote] = useState("");
  const latestLoad = useRef(0);
  const { confirm, dialog } = useConfirm();

  useEffect(() => {
    (async () => {
      try {
        const [groupList, playerList] = await Promise.all([api.listGroups(), api.listPlayers()]);
        const sorted = (groupList ?? []).slice().sort(byName);
        setGroups(sorted);
        setPlayers((playerList ?? []).slice().sort(byName));
        setGroupID(sorted[0]?.id ?? "");
      } catch (err: any) {
        console.error("Failed to load groups and players", err);
        setError(err?.message || "Не удалось загрузить группы");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadDuties = useCallback(async () => {
    if (!groupID) return;
    // Switching group or month quickly must not let an older answer win.
    const loadID = ++latestLoad.current;
    setDutiesLoading(true);
    try {
      const list = await gymDutiesApi.list(groupID, month || undefined);
      if (loadID !== latestLoad.current) return;
      setDuties((list ?? []).slice().sort((a, b) => a.date.localeCompare(b.date)));
    } catch (err: any) {
      if (loadID !== latestLoad.current) return;
      console.error("Failed to load gym duties", err);
      setDuties([]);
      setError(err?.message || "Не удалось загрузить дежурства");
    } finally {
      if (loadID === latestLoad.current) setDutiesLoading(false);
    }
  }, [groupID, month]);

  useEffect(() => {
    loadDuties();
  }, [loadDuties]);

  const playerName = new Map(players.map((p) => [p.id, p.name]));
  const groupPlayers = players.filter((p) => p.is_active && p.group_id === groupID);

  const changeGroup = (id: string) => {
    setGroupID(id);
    setPlayerID("");
    setError(null);
  };

  const create = async (e: FormEvent) => {
    e.preventDefault();
    if (!date || !groupID) return;
    setBusy(true);
    setError(null);
    try {
      await gymDutiesApi.create({
        date,
        group_id: groupID,
        player_id: playerID || null,
        note: note.trim() || null,
      });
      setDate("");
      setPlayerID("");
      setNote("");
      // Show the month the new duty landed in; a month change reloads by itself.
      const dutyMonth = date.slice(0, 7);
      if (month && month !== dutyMonth) setMonth(dutyMonth);
      else await loadDuties();
    } catch (err: any) {
      console.error("Failed to create gym duty", err);
      setError(err?.message || "Не удалось добавить дежурство");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (duty: GymDuty) => {
    const name = duty.player_id ? playerName.get(duty.player_id) ?? "Ребёнок" : null;
    const confirmed = await confirm({
      title: `Удалить дежурство ${formatDate(duty.date)}?`,
      body: name ? (
        <p>
          {name} больше не дежурит в этот день. Приложение не сообщит об отмене — предупредите
          ребёнка сами.
        </p>
      ) : (
        <p>Свободное место пропадёт из приложения.</p>
      ),
      confirmLabel: "Удалить дежурство",
      danger: true,
    });
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      await gymDutiesApi.remove(duty.id);
    } catch (err: any) {
      console.error("Failed to delete gym duty", err);
      setError(err?.message || "Не удалось удалить дежурство");
    } finally {
      setBusy(false);
      await loadDuties();
    }
  };

  if (loading) return <PageLoader />;

  return (
    <div className="page page-wide">
      <h2>Дежурства</h2>
      <p className="page-description">
        Кто из детей дежурит в зале. Ребёнку приходит уведомление, когда его назначают. Свободное
        место дети занимают сами в приложении.
      </p>

      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}

      <div className="gd-filters">
        <label className="cp-field">
          <span>Группа</span>
          <select className="mav-input" value={groupID} onChange={(e) => changeGroup(e.target.value)}>
            {groups.length === 0 && <option value="">Групп нет</option>}
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </label>
        <label className="cp-field">
          <span>Месяц</span>
          <input type="month" className="mav-input" value={month} onChange={(e) => setMonth(e.target.value)} />
        </label>
      </div>
      <p className="mav-hint gd-filters-hint">Очистите месяц, чтобы увидеть все даты.</p>

      <form className="mav-card cp-form gd-form" aria-label="Новое дежурство" onSubmit={create}>
        <label className="cp-field">
          <span>Дата</span>
          <input
            type="date"
            className="mav-input"
            value={date}
            disabled={busy || !groupID}
            onChange={(e) => setDate(e.target.value)}
          />
        </label>
        <label className="cp-field">
          <span>Ребёнок</span>
          <select
            className="mav-input"
            value={playerID}
            disabled={busy || !groupID}
            onChange={(e) => setPlayerID(e.target.value)}
          >
            <option value="">Свободное место</option>
            {groupPlayers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label className="cp-field gd-note-field">
          <span>Заметка</span>
          <input
            className="mav-input"
            value={note}
            maxLength={200}
            disabled={busy || !groupID}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
        <div className="cp-actions">
          <button type="submit" className="mav-btn-primary" disabled={busy || !groupID || !date}>
            Добавить дежурство
          </button>
        </div>
      </form>

      <div className="table-wrap">
        <table className="settings-table stack-table gd-table">
          <thead>
            <tr>
              <th>Дата</th>
              <th>Ребёнок</th>
              <th>Кто записал</th>
              <th>Заметка</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {duties.length === 0 ? (
              <tr>
                <td colSpan={5} className="empty-cell">
                  {dutiesLoading ? "Загрузка…" : "Дежурств нет"}
                </td>
              </tr>
            ) : (
              duties.map((d) => (
                <tr key={d.id}>
                  <td className="date-cell gd-cell-date">{formatDate(d.date)}</td>
                  <td className="gd-cell-player">
                    {d.player_id ? (
                      playerName.get(d.player_id) ?? "Ребёнок не найден"
                    ) : (
                      <span className="mav-badge mav-badge-muted">Свободно</span>
                    )}
                  </td>
                  <td className="gd-cell-by">{assignedByLabel(d)}</td>
                  <td className="value-text gd-cell-note">{d.note || "—"}</td>
                  <td className="cell-action">
                    <button className="btn-quiet-danger" disabled={busy} onClick={() => remove(d)}>
                      Удалить
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {dialog}
    </div>
  );
}
